import { useNavigate } from "@tanstack/react-router";
import { type ReactNode } from "react";
/* The one file, not the marketing index: the root route reads this on every
   page, and the index re-exports the whole site's copy with it. */
import * as marketing from "@alm/domain/marketing/site-version";
import { isPeopleHost } from "@/lib/host/host-rewrite";
import {
  type SiteVersion,
  SiteVersionContext,
  useSiteBase,
} from "@/lib/marketing/site-version-shared";

export const { getSiteVersion } = marketing;

export function SiteVersionProvider({
  version,
  children,
}: {
  version: SiteVersion | null;
  children: ReactNode;
}) {
  return <SiteVersionContext.Provider value={version}>{children}</SiteVersionContext.Provider>;
}

/** The arm a lead should be attributed to: "main" or the version id.
 *  On PEOPLE's own domain the address bar never shows the `/v2` prefix
 *  (host-rewrite.ts), so the host, not the path, says which arm this is. */
export function currentSiteVersion(): string {
  if (typeof window === "undefined") return "main";
  if (isPeopleHost(window.location.hostname)) return "v2";
  return getSiteVersion(window.location.pathname) ?? "main";
}

/** The same prefixing as `MLink`, for the few places that have to move the reader from an event
 * handler rather than from a link. */
export function useMNavigate(): (href: string) => void {
  const base = useSiteBase();
  const navigate = useNavigate();
  return (href: string) => {
    const to = href === "/" ? base || "/" : `${base}${href}`;
    void navigate({ to } as Parameters<typeof navigate>[0]);
  };
}
